import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { getBlogDetails, deleteBlog } from "../apis/Blog";
import { toast } from "react-toastify";

const BlogDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchBlogDetails = async () => {
      try {
        setLoading(true);
        const response = await getBlogDetails(id);
        setBlog(response);
        setError(null);
      } catch (error) {
        setError("Failed to fetch blog details");
        toast.error("Failed to fetch blog details");
        console.error("Error fetching blog details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogDetails();
  }, [id]);

  const handleDelete = async () => {
    try {
      await deleteBlog(id);
      toast.success("Blog deleted successfully");
      navigate("/blogs");
    } catch (error) {
      toast.error("Failed to delete blog");
      console.error("Error deleting blog:", error);
    }
    setDeleteDialogOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !blog) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error || "Blog not found"}</Typography>
        <Button
          variant="outlined"
          onClick={() => navigate("/blogs")}
          sx={{ mt: 2 }}
        >
          Back to Blogs
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              {blog.title}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {new Date(blog.createdAt).toLocaleDateString()}
              </Typography>
              <Chip
                label={blog.status}
                color={blog.status === "Published" ? "success" : "warning"}
                size="small"
              />
            </Stack>
          </Box>
          <Box>
            <IconButton
              color="primary"
              onClick={() => navigate(`/blogs/edit/${blog.id}`)}
              sx={{ mr: 1 }}
            >
              <EditIcon />
            </IconButton>
            <IconButton color="error" onClick={() => setDeleteDialogOpen(true)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Blog</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this blog? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Gallery */}
        {blog.files && blog.files.length > 0 && (
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Files
            </Typography>
            <Grid container spacing={2}>
              {blog.files.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    component="img"
                    src={file.url}
                    alt={file.name}
                    sx={{
                      width: "100%",
                      height: 200,
                      objectFit: "contain",
                      borderRadius: 1,
                      backgroundColor: "grey.100",
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 4 }}>
          <div dangerouslySetInnerHTML={{ __html: blog.description }} />
        </Box>

        {/* FAQs Section */}
        {blog.faqs && blog.faqs.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Frequently Asked Questions
            </Typography>
            {blog.faqs.map((faq, index) => (
              <Accordion key={index}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`faq-${index}-content`}
                  id={`faq-${index}-header`}
                >
                  <Typography variant="subtitle1">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={() => navigate("/blogs")}>
            Back to Blogs
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BlogDetail;
